import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  CruiseSaleModel,
  CruiseSaleMutationInput,
} from 'api-hooks/sale/cruise';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { CustomLabel } from 'components/elements/custom-form-control';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import RescheduleHistoryTable from 'legacy-modules/sales/reschedule/reschedule-history-table';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  Container,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import CruiseSection from './cruise';
import {
  CruiseSaleCruiseSaleDetailsFormType,
  CruiseSaleCruiseSaleFormType,
  CruiseSaleCruiseSaleGuestFormType,
  CruiseSaleFormSchema,
  CruiseSaleFormType,
} from './form-type';
import useGetDefaultValues, {
  addNewDetailsItem,
  addNewGuestItem,
} from './hooks/use-get-default-values';
import SalesDetailCard from './sales-detail-card';
import CruiseSaleSelectInput from './select-input';
import SummaryCruiseSale from './summary';
import { totalCruiseSaleCalculation } from './total-calculation';
import { FormContent, FormDivider, OtherContainer } from '../style';

interface Props {
  cruiseSale?: CruiseSaleModel;
  onSubmit: (
    input: CruiseSaleMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<CruiseSaleModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: CruiseSaleFormType;
}

export default function CruiseSaleForm(props: Props) {
  const { t } = useTranslation();
  const { cruiseSale, renderError } = props;

  const { can } = useAuthorization();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    cruiseSale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(CruiseSaleFormSchema());

  const methods = useForm<CruiseSaleFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: CruiseSaleFormType) => {
      const {
        hasDueReceivable,
        cruiseSales,
        customerBillingOptions,
        address,
        phone,
        tempTransactionAt,
        customerStatus,
        remainingTransactionQuota,
        isExceedingQuota,
        // remove unused code
        ...rest
      } = values;
      try {
        const total = totalCruiseSaleCalculation({
          cruiseSales: values.cruiseSales,
          discount: values.discount,
          otherCost: values.otherCost,
        });

        const cs = cruiseSales.map((cs) => ({
          ...cs,
          cruiseSaleGuests: cs.cruiseSaleGuests.map((csg) => {
            const preferences = (csg.preferences || []).map(
              (preference) => preference.id,
            );

            return {
              ...csg,
              preferences,
            };
          }),
          cruiseSaleDetails: cs.isGroupedPrice
            ? [
                {
                  ...cs.cruiseSaleDetails[0],
                  guestId: '',
                  guestName: '',
                },
              ]
            : cs.cruiseSaleDetails,
        }));

        const needApproval =
          tempTransactionAt!.getTime() !== values.transactionAt!.getTime();

        const result = {
          ...rest,
          cruiseSales: cs,
          total,
          needApproval,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const isContribute =
    cruiseSale?.admin.id === employee?.id ||
    cruiseSale?.salesman?.id === employee?.id ||
    cruiseSale?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = cruiseSale && (isContribute || core) && (
    <>
      <SalesActionComponent
        data={cruiseSale}
        defaultValues={defaultValues}
        type="cruise-sales"
      />
    </>
  );

  const { setValue, getValues } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      const guestPreferences = guestPreferencesTransformer(guest);
      const cruiseSales: CruiseSaleCruiseSaleFormType[] = getValues(
        'cruiseSales',
      ).map((cs) => {
        const cruiseSaleGuests: CruiseSaleCruiseSaleGuestFormType[] = [
          ...cs.cruiseSaleGuests,
          {
            ...addNewGuestItem(),
            guestId: guest.id,
            guestName,
            guestPreferences,
            guestDetailId: guest.id,
          },
        ];

        const cruiseSaleDetails: CruiseSaleCruiseSaleDetailsFormType[] = [
          ...cs.cruiseSaleDetails,
          {
            ...addNewDetailsItem(),
            guestId: guest.id,
            guestName,
            guestDetailId: guest.id,
          },
        ];
        return {
          ...cs,
          cruiseSaleGuests,
          cruiseSaleDetails,
        };
      });
      setValue('cruiseSales', cruiseSales);
    },
    [getValues, setValue],
  );
  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: CruiseSaleModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!cruiseSale}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        cruiseSale && `${t('common:cruise_sale')} - ${cruiseSale.invoiceNumber}`
      }
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={cruiseSale}
              noSubmit
              noCancel
              noDelete
              noEdit
              title={
                cruiseSale ? (
                  <TitleStatus
                    type="sale-default"
                    isCompleted={cruiseSale.isCompleted}
                    status={cruiseSale.status}
                    title={`${t('common:cruise_sale')} - ${
                      cruiseSale.invoiceNumber
                    }`}
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:cruise_sale') })
                )
              }
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        {renderError}
        <div>
          {cruiseSale && !!(cruiseSale?.releaseSales || []).length && (
            <SalePurchaseRefundTable data={cruiseSale.releaseSales} />
          )}
          {cruiseSale &&
            !!cruiseSale.purchases.length &&
            (isContribute || core) && (
              <PurchaseRelatedTable
                data={cruiseSale.purchases}
                navigation="CruisePurchasesView"
              />
            )}
          {cruiseSale && (
            <RescheduleHistoryTable saleId={cruiseSale.id} type="cruise" hide />
          )}
          {cruiseSale &&
            (!!cruiseSale.parentSale ||
              !!(cruiseSale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={cruiseSale}
                navigation="CruiseSalesView"
              />
            )}
          <RawSectionContainer>
            <HalfContainer>
              <CruiseSaleSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
                isSales
                type="invoice"
              />
            </HalfContainer>
          </RawSectionContainer>
          <RawSectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketSales:group_code')}
                placeholder={t('ticketSales:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </RawSectionContainer>
          <SeparatorVertical $margin={16} />
          <SalesDetailCard cruiseSale={cruiseSale} />
          <SeparatorVertical $margin={46}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <CruiseSection />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <Container $style={{ height: 'auto' }}>
            <OtherContainer>
              <CustomLabel label={t('common:discount')} />
              <Input
                type="number"
                name="discount"
                // label={t('common:discount')}
                placeholder={t('common:discount')}
                // labelPlacement={TextLabelPlacementEnum.Left}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
            <SeparatorVertical $margin={16} />
            <OtherContainer>
              <CustomLabel label={t('common:other')} />
              <Input
                type="number"
                name="otherCost"
                // label={t('common:other')}
                placeholder={t('common:other')}
                // labelPlacement={TextLabelPlacementEnum.Left}
                textAlign="right"
                clearable={false}
                noMargin
              />
            </OtherContainer>
          </Container>
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryCruiseSale cruiseSale={cruiseSale} />
    </Form>
  );
}
